import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface RoadmapItem {
  id: string;
  title: string;
  description: string;
  status: 'planned' | 'in_progress' | 'completed';
  expected_timeline: string;
  expected_quarter: number;
  expected_year: number;
  category: string;
  priority: number;
  created_at: string;
  updated_at: string;
  published: boolean;
  votes_count: number;
  user_has_voted?: boolean;
}

export interface UseRoadmapReturn {
  roadmapItems: RoadmapItem[];
  inProgressItems: RoadmapItem[];
  plannedItems: RoadmapItem[];
  completedItems: RoadmapItem[];
  loading: boolean;
  error: string | null;
  voteForItem: (itemId: string) => Promise<boolean>;
  userVotes: string[];
  refreshRoadmap: () => Promise<void>;
}

export function useRoadmap(): UseRoadmapReturn {
  const [roadmapItems, setRoadmapItems] = useState<RoadmapItem[]>([]);
  const [userVotes, setUserVotes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch roadmap items from Supabase
  const fetchRoadmapItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Get roadmap items
      const { data, error: roadmapError } = await supabase
        .from('roadmap_items')
        .select('*')
        .eq('published', true)
        .order('priority', { ascending: true });
      
      if (roadmapError) {
        throw roadmapError;
      }
      
      // Get user's votes if authenticated
      const { data: session } = await supabase.auth.getSession();
      let userVoteIds: string[] = [];
      
      if (session.session) {
        const { data: voteData, error: voteError } = await supabase
          .from('item_votes')
          .select('roadmap_item_id')
          .eq('user_id', session.session.user.id);
        
        if (!voteError && voteData) {
          userVoteIds = voteData.map(vote => vote.roadmap_item_id);
          setUserVotes(userVoteIds);
        }
      }
      
      // Mark items the user has voted for
      const itemsWithVoteStatus = data?.map(item => ({
        ...item,
        user_has_voted: userVoteIds.includes(item.id)
      })) || [];
      
      setRoadmapItems(itemsWithVoteStatus);
    } catch (err) {
      console.error('Error fetching roadmap items:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch roadmap items');
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh roadmap data
  const refreshRoadmap = useCallback(async () => {
    await fetchRoadmapItems();
  }, [fetchRoadmapItems]);

  // Vote for an item
  const voteForItem = async (itemId: string): Promise<boolean> => {
    try {
      const { data: session } = await supabase.auth.getSession();
      
      if (!session.session) {
        setError('You must be logged in to vote');
        return false;
      }
      
      const userId = session.session.user.id;
      
      // Check if user has already voted for this item
      const hasVoted = userVotes.includes(itemId);
      
      if (hasVoted) {
        // Remove vote
        const { error: removeError } = await supabase
          .from('item_votes')
          .delete()
          .eq('user_id', userId)
          .eq('roadmap_item_id', itemId);
        
        if (removeError) {
          throw removeError;
        }
        
        // Update vote count on roadmap item
        const { error: updateError } = await supabase.rpc('decrement_vote_count', {
          item_id: itemId
        });
        
        if (updateError) {
          throw updateError;
        }
        
        // Update local state
        setUserVotes(prev => prev.filter(id => id !== itemId));
        setRoadmapItems(prev => 
          prev.map(item => 
            item.id === itemId 
              ? { ...item, votes_count: item.votes_count - 1, user_has_voted: false }
              : item
          )
        );
      } else {
        // Add vote
        const { error: addError } = await supabase
          .from('item_votes')
          .insert({
            user_id: userId,
            roadmap_item_id: itemId,
            created_at: new Date().toISOString()
          });
        
        if (addError) {
          throw addError;
        }
        
        // Update vote count on roadmap item
        const { error: updateError } = await supabase.rpc('increment_vote_count', {
          item_id: itemId
        });
        
        if (updateError) {
          throw updateError;
        }
        
        // Update local state
        setUserVotes(prev => [...prev, itemId]);
        setRoadmapItems(prev => 
          prev.map(item => 
            item.id === itemId 
              ? { ...item, votes_count: item.votes_count + 1, user_has_voted: true }
              : item
          )
        );
      }
      
      return true;
    } catch (err) {
      console.error('Error voting for item:', err);
      setError(err instanceof Error ? err.message : 'Failed to vote for item');
      return false;
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchRoadmapItems();
  }, [fetchRoadmapItems]);

  // Filter items by status
  const inProgressItems = roadmapItems.filter(item => item.status === 'in_progress');
  const plannedItems = roadmapItems.filter(item => item.status === 'planned');
  const completedItems = roadmapItems.filter(item => item.status === 'completed');

  return {
    roadmapItems,
    inProgressItems,
    plannedItems,
    completedItems,
    loading,
    error,
    voteForItem,
    userVotes,
    refreshRoadmap
  };
} 